import {
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  Divider,
  useDisclosure,
  Box,
  Spinner,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { Mail02 } from '@kula-ai/icons/dual-tone/Mail02';
import { ChevronDown } from '@kula-ai/icons/solid/ChevronDown';
// import { Slack as SlackLogo } from '@/assets/icons/colored';
import { Drawer } from '@/components/ats';
import PreRequisitesContent from './PreRequisitesContent';
import { useRef, useState } from 'react';
import { FormBuilder } from '@/components/formBuilder';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { useEmailFieldWithCreate } from '@/components/formBuilder/formFields/useEmailField/useEmailFieldWithCreate';
import { NodeViewWrapper } from '@tiptap/react';
import ReactDOMServer from 'react-dom/server';
import { selectUsers } from '@/store/users/selectors';
import { formatDNSRecords } from './dnsRecordsHelpers';
import { useShowToast } from '@/hooks';
import { SHARED_EMAIL_API_SERVICES } from '../api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { domainValueContext } from './SharedEmailCard';
import { CenterSpinner } from '@/components/ats';
import { FORWARDING_LINK } from './constants';
import { MX_PROVIDERS } from './constants';
interface OptionConfig {
  value: number;
  label: string;
  icon?: React.ComponentType<any>;
  onClick?: () => void;
}

interface PopoverActions {
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onToggle?: () => void;
}

interface RenderOptionsProps {
  popoverActions?: PopoverActions;
  onSelect?: (option: OptionConfig) => void;
  optionsConfig?: OptionConfig[];
}

interface RenderComponentProps {
  popoverActions?: PopoverActions;
  key?: string | number;
  optionsConfig?: OptionConfig[];
  domainId?: string | number;
  domainDetails?: any;
  sharedEmailData?: any;
  sentTOITModalActions?: any;
  triggerRef?: string;
  createUpdateEmailDrawerActions?: any;
  preRequisitesModalOnClose?: () => void;
  refetch?: () => void;
  pointerEvents?: string;
  hasEmails?: boolean;
}

const tableBaseStyle: React.CSSProperties = {
  borderCollapse: 'collapse',
  width: '100%',
  overflow: 'auto',
  marginTop: '16px',
  zIndex: 1,
  userSelect: 'none',
  border: '1px solid #E9EBF4',
};

const thTdStyle: React.CSSProperties = {
  border: '1px solid #E9EBF4',
  padding: '8px',
  textAlign: 'start',
  minWidth: '80px',
  fontSize: '14px',
};

const thStyle: React.CSSProperties = {
  ...thTdStyle,
  color: '#413B74',
};

export const TableContent = () => {
  const domainDetails = useContext(domainValueContext);
  const toast = useShowToast();

  const { data: recordsTable, isLoading } = useQuery({
    queryKey: ['domain-dns', domainDetails?.domainDetails?.id],
    queryFn: async () => {
      const data = await SHARED_EMAIL_API_SERVICES?.getDomainDNSRecords({
        id: domainDetails?.domainDetails?.id,
      });
      if (!data || Object.keys(data).length === 0) {
        toast({
          title: 'Failed to get DNS records',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      return data;
    },
  });

  const forwardingEmails =
    domainDetails?.sharedEmailData?.shared_emails
      ?.filter((email) => email?.domain === domainDetails?.domainDetails?.id)
      ?.map((email) => email?.email)
      ?.join(', ') || '';

  const primaryEmail = domainDetails?.sharedEmailData?.shared_emails?.find(
    (email) => email?.is_primary
  );
  const forwardingEmail = 'forward@' + primaryEmail?.email?.split('@')[1];
  if (isLoading) {
    return (
      <NodeViewWrapper>
        <CenterSpinner props={{ display: 'box' }} />
      </NodeViewWrapper>
    );
  }

  const MXRecords =
    recordsTable?.dns_records?.receiving?.map((record) => ({
      Host: record?.name || '-',
      RequiredValue: record?.value || '-',
      Type: record?.type || '-',
      Status: record?.is_active || '-',
      Valid: record?.valid || 'unknown',
    })) || [];

  const formattedRecordsTable = formatDNSRecords(recordsTable);

  const mxRecordsTable = [...MXRecords];

  const headers = [
    { label: 'Type', key: 'Type' },
    { label: 'Host', key: 'Host' },
    { label: 'Required value', key: 'RequiredValue' },
  ];
  return (
    <NodeViewWrapper>
      <Box
        p="16px"
        background={'secondary-25'}
        backgroundColor={'secondary-25'}
        borderRadius="6px"
        mb="8px"
      >
        <div
          style={{
            fontFamily: 'Arial, sans-serif',
            fontSize: '16px',
            fontWeight: 650,
            lineHeight: 1.5,
            display: 'flex',
            gap: '6px',
            marginTop: '16px',
            color: '#413B74',
          }}
        >
          <>
            {recordsTable?.mx_provider !== MX_PROVIDERS?.NOT_FOUND &&
              recordsTable?.mx_provider !== MX_PROVIDERS?.MAILGUN && (
                <span>1.</span>
              )}
            <span>Add the following entries to your DNS records</span>
          </>
        </div>
        <table style={tableBaseStyle}>
          <thead>
            <tr>
              {headers.map((header) => (
                <th key={header.key} style={thStyle}>
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {formattedRecordsTable.map((record, index) => (
              <tr key={index}>
                {headers.map((header) => (
                  <td
                    key={`${index}-${header.key}`}
                    style={thTdStyle}
                    title={
                      header.key === 'RequiredValue' ? record[header.key] : ''
                    }
                  >
                    {record[header.key] !== undefined &&
                    record[header.key] !== null
                      ? typeof record[header.key] === 'boolean'
                        ? record[header.key].toString()
                        : header.key === 'Type'
                        ? record[header.key].toUpperCase()
                        : record[header.key]
                      : '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {recordsTable?.mx_provider !== MX_PROVIDERS?.NOT_FOUND &&
          recordsTable?.mx_provider !== MX_PROVIDERS?.MAILGUN && (
            <>
              <p
                style={{
                  fontSize: '14px',
                  margin: '12px 26px',
                }}
              >
                <b>Note:</b> You can add Kula's MX records to your DNS or create
                a forwarding rule with your current email provider to receive
                replies from candidates{' '}
              </p>
              <table style={tableBaseStyle}>
                <thead>
                  <tr>
                    {headers.map((header) => (
                      <th key={header.key} style={thStyle}>
                        {header.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mxRecordsTable.map((record, index) => (
                    <tr key={index}>
                      {headers.map((header) => (
                        <td
                          key={`${index}-${header.key}`}
                          style={thTdStyle}
                          title={
                            header.key === 'RequiredValue'
                              ? record[header.key]
                              : ''
                          }
                        >
                          {record[header.key] !== undefined &&
                          record[header.key] !== null
                            ? typeof record[header.key] === 'boolean'
                              ? record[header.key].toString()
                              : header.key === 'Type'
                              ? record[header.key].toUpperCase()
                              : record[header.key]
                            : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              {recordsTable?.mx_provider === MX_PROVIDERS?.UNKNOWN && (
                <>
                  <p
                    style={{
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '16px',
                      fontWeight: 650,
                      lineHeight: 1.5,
                      display: 'flex',
                      gap: '6px',
                      marginTop: '16px',
                      color: '#413B74',
                    }}
                  >
                    2. Set up a forwarding rule
                  </p>
                  <p style={{ lineHeight: 1.5 }}>
                    Set up the forwarding rule from
                    <span style={{ color: '#503BF0' }}>
                      {forwardingEmails}
                    </span>{' '}
                    to{' '}
                    <span style={{ color: '#503BF0' }}> {forwardingEmail}</span>
                    in your email provider's admin settings and ensure all
                    emails are forwarded to{' '}
                    <span
                      style={{
                        color: '#503BF0',
                        marginRight: '2px',
                        marginLeft: '2px',
                      }}
                    >
                      {forwardingEmail}
                    </span>
                    .
                  </p>
                </>
              )}
              <br></br>
            </>
          )}
        {recordsTable?.mx_provider === MX_PROVIDERS?.MICROSOFT && (
          <>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '16px',
                fontWeight: 650,
                lineHeight: 1.5,
                display: 'flex',
                gap: '6px',
                marginTop: '16px',
                color: '#413B74',
              }}
            >
              2. Set up a forwarding rule
            </p>

            <p style={{ lineHeight: 1.5 }}>
              Follow the instructions in the link below to set up the forwarding
              rule from{' '}
              <span style={{ color: '#503BF0' }}>{forwardingEmails}</span> to{' '}
              <span style={{ color: '#503BF0', marginBottom: '8px' }}>
                {forwardingEmail}
              </span>
              . <br></br>
              <a
                href={FORWARDING_LINK.microsoft}
                style={{ color: '#503BF0' }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {FORWARDING_LINK.microsoft}{' '}
              </a>
            </p>
            <br></br>
          </>
        )}
        {recordsTable?.mx_provider === MX_PROVIDERS?.GOOGLE && (
          <>
            <>
              <p
                style={{
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '16px',
                  fontWeight: 650,
                  lineHeight: 1.5,
                  display: 'flex',
                  gap: '6px',
                  marginTop: '16px',
                  color: '#413B74',
                }}
              >
                2. Set up a forwarding rule
              </p>

              <p style={{ lineHeight: 1.5 }}>
                Follow the instructions in the link below to set up the
                forwarding rule from{' '}
                <span style={{ color: '#503BF0' }}>{forwardingEmails}</span> to{' '}
                <span style={{ color: '#503BF0' }}>{forwardingEmail}</span>.
                Ensure 'Messages affected' is set to 'All messages'. <br></br>
                <a
                  href={FORWARDING_LINK.google}
                  style={{ color: '#503BF0' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {FORWARDING_LINK.google}{' '}
                </a>
              </p>
              <br></br>
            </>
          </>
        )}
      </Box>
    </NodeViewWrapper>
  );
};

const RenderOptions: React.FC<RenderOptionsProps> = ({
  popoverActions,
  onSelect = undefined,
  optionsConfig,
}) => {
  return (
    <Flex flexDir="column">
      {optionsConfig.map((option) => (
        <Flex
          key={option.value}
          gap="md"
          borderBottom="1px solid"
          borderColor="secondary-100"
          _hover={{
            bgColor: 'secondary-25',
          }}
          p="12px"
          cursor="pointer"
          onClick={() => {
            option.onClick?.();
          }}
          borderRadius="6px"
        >
          {option?.icon && <Icon as={option.icon} w="20px" h="20px"></Icon>}
          <KText size="sm-medium">{option.label}</KText>
        </Flex>
      ))}
    </Flex>
  );
};

const RenderSendToITDeptBtn: React.FC<RenderComponentProps> = ({
  popoverActions,
  key,
  optionsConfig,
  sentTOITModalActions,
  domainDetails,
  triggerRef,
  createUpdateEmailDrawerActions,
  preRequisitesModalOnClose,
  refetch = () => {},
  hasEmails,
}) => {
  const formRef = useRef(null);
  const currUser = useSelector(selectCurrUser);
  const allUsers = useSelector(selectUsers);
  const toast = useShowToast();
  const domainData = useContext(domainValueContext);

  const originIsFromCreateDrawer = triggerRef === 'createEmailDrawer';
  const emailFieldConfigs = useEmailFieldWithCreate({
    formRef,
    replaceContent: false,
    getEditorInstance: true,
    enableToAddress: true,
  });

  const [recordsTable, setRecordsTable] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchDNSRecords = async () => {
    setIsLoading(true);
    try {
      const data = await SHARED_EMAIL_API_SERVICES?.getDomainDNSRecords({
        id: domainDetails?.id,
      });
      if (!data || Object.keys(data).length === 0) {
        toast({
          title: 'Failed to get DNS records',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      setRecordsTable(data);
    } catch (error) {
      toast({
        title: 'Failed to get DNS records',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateDNSTableHTML = () => {
    if (isLoading) {
      return (
        <NodeViewWrapper>
          <CenterSpinner />
        </NodeViewWrapper>
      );
    }

    const forwardingEmails =
      domainData?.sharedEmailData?.shared_emails
        ?.filter((email) => email?.domain === domainData?.domainDetails?.id)
        ?.map((email) => email?.email)
        ?.join(', ') || '';

    const MXRecords =
      recordsTable?.dns_records?.receiving?.map((record) => ({
        Host: record?.name || '-',
        RequiredValue: record?.value || '-',
        Type: record?.type || '-',
        Status: record?.is_active || '-',
        Valid: record?.valid || 'unknown',
      })) || [];

    const formattedRecordsTable = formatDNSRecords(recordsTable) || [];
    const mxRecordsTable = [...(MXRecords || [])];

    const headers = [
      { label: 'Type', key: 'Type' },
      { label: 'Host', key: 'Host' },
      { label: 'Required value', key: 'RequiredValue' },
    ];
    const primaryEmail = domainData?.sharedEmailData?.shared_emails?.find(
      (email) => email?.is_primary
    );
    const forwardingEmail = 'forward@' + primaryEmail?.email?.split('@')[1];
    const tableHTMLContent = ReactDOMServer.renderToStaticMarkup(
      <Box
        p="16px"
        background={'secondary-25'}
        backgroundColor={'secondary-25'}
        borderRadius="6px"
        mb="8px"
      >
        <div
          style={{
            fontFamily: 'Arial, sans-serif',
            fontSize: '16px',
            fontWeight: 650,
            lineHeight: 1.5,
            display: 'flex',
            gap: '6px',
            marginTop: '16px',
            color: '#413B74',
          }}
        >
          <>
            {recordsTable?.mx_provider !== MX_PROVIDERS?.NOT_FOUND &&
              recordsTable?.mx_provider !== MX_PROVIDERS?.MAILGUN && (
                <span>1.</span>
              )}
            <span>Add the following entries to your DNS records</span>
          </>
        </div>
        <table style={tableBaseStyle}>
          <thead>
            <tr>
              {headers.map((header) => (
                <th key={header.key} style={thStyle}>
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {formattedRecordsTable.map((record, index) => (
              <tr key={index}>
                {headers.map((header) => (
                  <td
                    key={`${index}-${header.key}`}
                    style={thTdStyle}
                    title={
                      header.key === 'RequiredValue' ? record[header.key] : ''
                    }
                  >
                    <pre>
                      {record[header.key] !== undefined &&
                      record[header.key] !== null
                        ? typeof record[header.key] === 'boolean'
                          ? record[header.key].toString()
                          : header.key === 'Type'
                          ? record[header.key].toUpperCase()
                          : record[header.key]
                        : '-'}
                    </pre>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {recordsTable?.mx_provider !== null &&
          recordsTable?.mx_provider !== MX_PROVIDERS?.NOT_FOUND &&
          recordsTable?.mx_provider !== MX_PROVIDERS?.MAILGUN && (
            <>
              <p
                style={{
                  fontSize: '14px',
                  margin: '12px 26px',
                }}
              >
                <b>Note:</b> You can add Kula's MX records to your DNS or create
                a forwarding rule with your current email provider to receive
                replies from candidates{' '}
              </p>
              <table style={tableBaseStyle}>
                <thead>
                  <tr>
                    {headers.map((header) => (
                      <th key={header.key} style={thStyle}>
                        {header.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mxRecordsTable.map((record, index) => (
                    <tr key={index}>
                      {headers.map((header) => (
                        <td
                          key={`${index}-${header.key}`}
                          style={thTdStyle}
                          title={
                            header.key === 'RequiredValue'
                              ? record[header.key]
                              : ''
                          }
                        >
                          <pre>
                            {record[header.key] !== undefined &&
                            record[header.key] !== null
                              ? typeof record[header.key] === 'boolean'
                                ? record[header.key].toString()
                                : header.key === 'Type'
                                ? record[header.key].toUpperCase()
                                : record[header.key]
                              : '-'}
                          </pre>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              {recordsTable?.mx_provider === MX_PROVIDERS?.UNKNOWN && (
                <>
                  <p
                    style={{
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '16px',
                      fontWeight: 650,
                      lineHeight: 1.5,
                      display: 'flex',
                      gap: '6px',
                      marginTop: '16px',
                      color: '#413B74',
                    }}
                  >
                    2. Set up a forwarding rule
                  </p>
                  <p style={{ lineHeight: 1.5 }}>
                    Set up the forwarding rule from
                    <span style={{ color: '#503BF0' }}>
                      {forwardingEmails}
                    </span>{' '}
                    to{' '}
                    <span style={{ color: '#503BF0' }}> {forwardingEmail}</span>
                    in your email provider's admin settings and ensure all
                    emails are forwarded to{' '}
                    <span
                      style={{
                        color: '#503BF0',
                        marginRight: '2px',
                        marginLeft: '2px',
                      }}
                    >
                      {forwardingEmail}
                    </span>
                    .
                  </p>
                </>
              )}
              <br></br>
            </>
          )}
        {recordsTable?.mx_provider === MX_PROVIDERS?.MICROSOFT && (
          <>
            <p
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '16px',
                fontWeight: 650,
                lineHeight: 1.5,
                display: 'flex',
                gap: '6px',
                marginTop: '16px',
                color: '#413B74',
              }}
            >
              2. Set up a forwarding rule
            </p>

            <p style={{ lineHeight: 1.5 }}>
              Follow the instructions in the link below to set up the forwarding
              rule from{' '}
              <span style={{ color: '#503BF0' }}>{forwardingEmails}</span> to{' '}
              <span style={{ color: '#503BF0', marginBottom: '8px' }}>
                {forwardingEmail}
              </span>
              . <br></br>
              <a
                href={FORWARDING_LINK.microsoft}
                style={{ color: '#503BF0' }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {FORWARDING_LINK.microsoft}{' '}
              </a>
            </p>
            <br></br>
          </>
        )}
        {recordsTable?.mx_provider === MX_PROVIDERS?.GOOGLE && (
          <>
            <>
              <p
                style={{
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '16px',
                  fontWeight: 650,
                  lineHeight: 1.5,
                  display: 'flex',
                  gap: '6px',
                  marginTop: '16px',
                  color: '#413B74',
                }}
              >
                2. Set up a forwarding rule
              </p>

              <p style={{ lineHeight: 1.5 }}>
                Follow the instructions in the link below to set up the
                forwarding rule from{' '}
                <span style={{ color: '#503BF0' }}>{forwardingEmails}</span> to{' '}
                <span style={{ color: '#503BF0' }}>{forwardingEmail}</span>.
                Ensure 'Messages affected' is set to 'All messages'. <br></br>
                <a
                  href={FORWARDING_LINK.google}
                  style={{ color: '#503BF0' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {FORWARDING_LINK.google}{' '}
                </a>
              </p>
              <br></br>
            </>
          </>
        )}
      </Box>
    );

    return tableHTMLContent;
  };

  const DnsInstructionsComponent = () => {
    return (
      <div className="tiptap-editor-content">
        <p
          style={{
            fontFamily: 'Arial, sans-serif',
            fontSize: '14px',
            marginBottom: '16px',
            lineHeight: 1.5,
          }}
        >
          Hello!
          <br />
          <br />
          Your organization is using Kula (kula.ai) as their recruitment
          platform to streamline hiring processes and find top talent. Kula
          allows your talent team to email candidates and prospects directly
          from within the platform.
          <br />
          <br />
          For these emails to be delivered properly, your DNS records need to be
          updated with specific TXT, CNAME, and MX entries. Organizations that
          skip this step often experience issues with emails being delayed or
          not reaching candidates at all.
          <br />
          <br />
          Please review the DNS entries (including type, domain, and value)
          provided below and implement them as recommended.
          <br />
          <br />
          Additionally, Kula's MX records can be added to the DNS or a
          forwarding rule can be set up in your email provider to ensure
          candidate replies are tracked.
          <br></br>
        </p>
      </div>
    );
  };

  const dnsInstructionRawHtmlContent = ReactDOMServer.renderToStaticMarkup(
    <DnsInstructionsComponent />
  );

  const formConfig = [
    {
      id: 'emailDetails',
      type: 'wrapper',
      wrapperProps: {
        title: 'MESSAGE',
        isHeaderClickable: true,
        defaultIsOpen: true,
        disablePlugins: ['link'],
      },
      fields: [...emailFieldConfigs],
    },
  ];

  const submitFormsAndGetErrors = async () => {
    if (formRef?.current) {
      const [key, value] = Object.entries(formRef?.current?.errors)?.[0] || []; //eslint-disable-line
      if (key) {
        var element = document.getElementById(key);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
      await formRef?.current?.submitForm();

      return !formRef?.current?.isValid;
    }
  };

  const sendEmailToITDept = useMutation({
    mutationFn: async (data: {
      from: any;
      to: any;
      cc: any;
      bcc: any;
      body: any;
      subject: any;
    }) => {
      const response = await SHARED_EMAIL_API_SERVICES.sendDNSRecordsViaEmail(
        domainDetails?.id,
        data
      );
      return response;
    },
  });

  const handleOpenSendToITModal = async () => {
    if (!recordsTable) {
      await fetchDNSRecords();
    }
    sentTOITModalActions?.onOpen();
  };

  const handleSendEmail = async () => {
    const errors = await submitFormsAndGetErrors();
    if (errors) return;

    if (!recordsTable) {
      await fetchDNSRecords();
    }

    const values = formRef?.current?.values;

    const resolveAddresses = (addresses, users) => {
      return addresses
        .map((item) => {
          if (typeof item === 'number') {
            const user = users.find((u) => u.id === item);
            return user?.email || null;
          }
          return item;
        })
        .filter((email) => email);
    };

    const { toAddress, cc, bcc } = values || {};

    const completeEmailBody = values?.body.replace(
      '<tiptap-editor-style></tiptap-editor-style>',
      generateDNSTableHTML()
    );

    const missingFields = [];

    if (!toAddress) {
      missingFields.push('To address');
    }
    if (!values?.subject) {
      missingFields.push('Subject');
    }
    if (!completeEmailBody) {
      missingFields.push('Body');
    }

    if (missingFields.length > 0) {
      toast({
        title: `${missingFields.join(', ')} ${
          missingFields.length > 1 ? 'are' : 'is'
        } required`,
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const formattedValues = {
      from: values?.from,
      to: toAddress ? resolveAddresses(toAddress, allUsers) : [],
      cc: cc ? resolveAddresses(cc, allUsers) : [],
      bcc: bcc ? resolveAddresses(bcc, allUsers) : [],
      body: completeEmailBody,
      subject: values?.subject,
    };

    try {
      await sendEmailToITDept.mutateAsync(formattedValues);
      toast({
        title: 'Email to the IT department sent successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      handleSendToITModalClose();
    } catch (error) {
      toast({
        title: 'Failed to send email',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      console.error('Error sending email:', error);
    }
  };

  // const handleSendToITModalClose = () => {
  //   sentTOITModalActions?.onClose();
  //   createUpdateEmailDrawerActions?.onClose();
  //   preRequisitesModalOnClose();
  // };

  const handleSendToITModalClose = () => {
    sentTOITModalActions?.onClose();
  };

  return (
    <>
      <Flex
        justifyContent="space-between"
        border={!originIsFromCreateDrawer && '1px solid'}
        borderRadius="6px"
        borderColor={!originIsFromCreateDrawer && 'primary-600'}
        bg="base-white"
        position="relative"
        key={key}
      >
        <Flex
          alignItems="center"
          py="4px"
          px="8px"
          gap="6px"
          w="100%"
          justifyContent="center"
          borderLeftRadius="6px"
          background={originIsFromCreateDrawer ? 'primary-600' : 'transparent'}
          cursor={'pointer'}
          onClick={() => {
            handleOpenSendToITModal();
          }}
          pointerEvents={
            originIsFromCreateDrawer || hasEmails ? 'auto' : 'none'
          }
        >
          <Icon
            color={originIsFromCreateDrawer ? 'base-white' : 'primary-500'}
            as={Mail02}
          />
          {isLoading ? (
            <Spinner
              size="xs"
              mx="84px"
              my="5px"
              color="primary-500"
              thickness="2px"
            />
          ) : (
            <KText
              color={originIsFromCreateDrawer ? 'base-white' : 'primary-500'}
              userSelect="none"
            >
              Send to your IT department{' '}
            </KText>
          )}
        </Flex>

        <Menu
          isOpen={popoverActions?.isOpen}
          onClose={() => popoverActions?.onClose()}
          closeOnBlur
          key={key}
        >
          <MenuButton
            background={originIsFromCreateDrawer ? 'primary-600' : 'base-white'}
            borderRightRadius="6px"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              popoverActions?.onToggle?.();
            }}
          >
            <Flex w="30px" alignItems="center" justifyContent="center" p="4px">
              <Icon
                color={originIsFromCreateDrawer ? 'base-white' : 'primary-500'}
                as={ChevronDown}
              />
            </Flex>
          </MenuButton>
          <MenuList
            p="0"
            border="1px solid"
            borderRadius="6px"
            borderColor={'var(--kula-colors-secondary-100) !important'}
          >
            <RenderOptions
              popoverActions={popoverActions}
              optionsConfig={optionsConfig}
            />
          </MenuList>
        </Menu>
        <Divider
          position="absolute"
          color="primary-500"
          orientation="vertical"
          right="29px"
          top="0"
          opacity="1"
        />
      </Flex>
      {sentTOITModalActions.isOpen && (
        <Drawer
          drawerActions={{
            isOpen: sentTOITModalActions?.isOpen,
            onClose: handleSendToITModalClose,
          }}
          headerText="Email your IT department"
          headerDescription="Request your IT department to complete the prerequisites for using the shared email"
          drawerContentProps={{ minW: '680px' }}
          drawerFooterProps={{
            backgroundColor: 'secondary-25',
          }}
          ctaLabel="Send"
          isCtaLoading={sendEmailToITDept?.isPending}
          ctaProps={{
            isLoading: sendEmailToITDept?.isPending,
            loadingText: 'Sending',
          }}
          onDrawerSubmit={handleSendEmail}
        >
          <Box background={'secondary-25'} h="100%" py="12px">
            <Box
              h="100%"
              mx="12px"
              overflowY="auto"
              borderRadius="6px"
              border="1px solid #E2E8F0"
              background={'base-white'}
            >
              <FormBuilder
                initialValues={{
                  from: currUser?.id,
                  body: `${dnsInstructionRawHtmlContent}<tiptap-editor-style></tiptap-editor-style>`,
                  subject: 'Action required from the IT department',
                }}
                config={formConfig}
                formRef={formRef}
              />
            </Box>
          </Box>
        </Drawer>
      )}
    </>
  );
};

export const RenderVerification: React.FC<RenderComponentProps> = ({
  popoverActions,
  key,
  triggerRef,
  domainDetails,
  createUpdateEmailDrawerActions,
  sharedEmailData,
  hasEmails,
}) => {
  const {
    isOpen: preRequisitesModalIsOpen,
    onOpen: preRequisitesModalOnOpen,
    onClose: preRequisitesModalOnClose,
  } = useDisclosure();

  const sentTOITModalActions = useDisclosure();
  const [dnsRecords, setDnsRecords] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useShowToast();

  // Function to fetch DNS records
  const fetchDNSRecords = async () => {
    setIsLoading(true);
    try {
      const data = await SHARED_EMAIL_API_SERVICES?.getDomainDNSRecords({
        id: domainDetails?.id,
      });
      if (!data || Object.keys(data).length === 0) {
        toast({
          title: 'Failed to get DNS records',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      setDnsRecords(data);
      return data;
    } catch (error) {
      toast({
        title: 'Failed to get DNS records',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle opening the "View instructions" modal
  const handleOpenInstructions = async () => {
    await fetchDNSRecords();
    preRequisitesModalOnOpen();
  };

  const optionsConfig: OptionConfig[] = [
    {
      value: 1,
      label: 'View instructions',
      onClick: () => {
        handleOpenInstructions();
      },
    },
  ];

  return (
    <>
      <Flex py="0.75rem" key={key}>
        <Flex gap="6px">
          <RenderSendToITDeptBtn
            popoverActions={popoverActions}
            key={key}
            optionsConfig={optionsConfig}
            sentTOITModalActions={sentTOITModalActions}
            triggerRef={triggerRef}
            domainDetails={{ ...domainDetails, dnsRecords }}
            createUpdateEmailDrawerActions={createUpdateEmailDrawerActions}
            preRequisitesModalOnClose={preRequisitesModalOnClose}
            hasEmails={hasEmails}
          ></RenderSendToITDeptBtn>
        </Flex>
      </Flex>
      {preRequisitesModalIsOpen && (
        <Drawer
          drawerActions={{
            isOpen: preRequisitesModalIsOpen,
            onClose: preRequisitesModalOnClose,
          }}
          headerText="Pre-requisites for using shared emails"
          headerTextProps={{
            borderBottom: '1px solid',
            borderColor: 'border-color',
          }}
          drawerContentProps={{ minW: '1078px' }}
          drawerBodyProps={{ overflow: 'scroll' }}
          hasFooterDivider={true}
          ctaLabel="Send to your IT department"
          onDrawerSubmit={() => {
            sentTOITModalActions?.onOpen();
          }}
        >
          {isLoading ? (
            <CenterSpinner />
          ) : (
            <PreRequisitesContent
              domainDetails={{ ...domainDetails, dnsRecords }}
              sharedEmailData={sharedEmailData}
            />
          )}
        </Drawer>
      )}
    </>
  );
};
